export default class SnapshotManager {
    #thumbnailModule = null
    static instance: SnapshotManager
    static getInstance() {
        if (!this.instance) {
            this.instance = new SnapshotManager()
        }
        return this.instance
    }

    constructor() {
        this.#thumbnailModule = window.ThumbnailModule()
    }

    generateSnapshotForXMPP = async ({ recordCanvas, isFree = false }) => {
        if (!recordCanvas) {
            return
        }
        return new Promise((res, rej) => {
            let smallSnapshotWidth = 240
            let smallSnapshotHeight = 120
            if (!isFree) {
                smallSnapshotWidth = 480
                smallSnapshotHeight = 240
            }

            if (recordCanvas instanceof HTMLCanvasElement) {
                recordCanvas.videoWidth = recordCanvas.width
                recordCanvas.videoHeight = recordCanvas.height
            }

            const canvas = this.#generateScaledSnapshotCanvasFromVideo({
                video: recordCanvas,
                width: smallSnapshotWidth,
                height: smallSnapshotHeight,
            })
            if (!canvas) {
                return
            }

            canvas.toBlob(
                function (blob) {
                    var reader = new FileReader()
                    // this.#downloadBlob({
                    // 	name: "xmpp.jpg",
                    // 	blob
                    // });
                    try {
                        reader.readAsDataURL(blob)
                    } catch (error) {
                        rej(error)
                    }
                    reader.onloadend = function () {
                        // remove header
                        var base64data = reader.result.replace(
                            /^data:image\/jpeg;base64,/,
                            ''
                        )
                        res(base64data)
                    }
                },
                'image/jpeg',
                0.4
            )
        })
    }

    generateSnapshotForEvent = async ({ video_or_canvas, isFree }) => {
        if (video_or_canvas instanceof HTMLCanvasElement) {
            video_or_canvas.videoWidth = video_or_canvas.width
            video_or_canvas.videoHeight = video_or_canvas.height
        }

        return new Promise(async (res, rej) => {
            const thumbnailBlob = await this.#generateThumbnailBlobForEvent({
                video: video_or_canvas,
                isFree,
            })
            const snapshotBlob = await this.#generateSnapshotBlobForEvent({
                video: video_or_canvas,
                isFree,
            })
            const concatedSnapshot = await this.#generateConcatedSnapshot({
                thumbnailBlob,
                snapshotBlob,
            })
            res(concatedSnapshot)
        })
    }

    #generateConcatedSnapshot = async ({ thumbnailBlob, snapshotBlob }) => {
        let srcBuffer, thumbnailBuffer, dstBuffer, dstSizeBuffer
        try {
            const thumbnailSource = await thumbnailBlob.arrayBuffer()
            const snapshotSource = await snapshotBlob.arrayBuffer()

            const src = new Uint8Array(snapshotSource)
            const src_size = src.length * src.BYTES_PER_ELEMENT
            const thumbnail = new Uint8Array(thumbnailSource)
            const thumbnail_size =
                thumbnail.length * thumbnail.BYTES_PER_ELEMENT

            // pass down reference
            const dst = new Int32Array(1)
            const dst_size = new Int32Array(1)

            srcBuffer = this.#thumbnailModule._malloc(
                src.length * src.BYTES_PER_ELEMENT
            )
            thumbnailBuffer = this.#thumbnailModule._malloc(
                thumbnail.length * thumbnail.BYTES_PER_ELEMENT
            )
            dstBuffer = this.#thumbnailModule._malloc(
                dst.length * dst.BYTES_PER_ELEMENT
            )
            dstSizeBuffer = this.#thumbnailModule._malloc(
                dst_size.length * dst_size.BYTES_PER_ELEMENT
            )

            this.#thumbnailModule.HEAPU8.set(src, srcBuffer)
            this.#thumbnailModule.HEAPU8.set(thumbnail, thumbnailBuffer)
            this.#thumbnailModule.HEAP32.set(dst, dstBuffer >> 2)
            this.#thumbnailModule.HEAP32.set(dst_size, dstSizeBuffer >> 2)

            const thumbnailStartPos = this.#thumbnailModule._addJpegThumbnail(
                srcBuffer,
                src_size,
                thumbnailBuffer,
                thumbnail_size,
                dstBuffer,
                dstSizeBuffer
            )

            // get value from dst_size reference
            const imageSize = this.#thumbnailModule.HEAP32[
                dstSizeBuffer / Int32Array.BYTES_PER_ELEMENT
            ]
            const dstOffset = this.#thumbnailModule.HEAP32[
                dstBuffer / Int32Array.BYTES_PER_ELEMENT
            ]

            const concatImage = new Uint8Array(imageSize)

            for (let v = 0; v < imageSize; v++) {
                concatImage[v] = this.#thumbnailModule.HEAPU8[
                    dstOffset / Uint8Array.BYTES_PER_ELEMENT + v
                ]
            }
            const data = new Blob([concatImage], {
                type: 'image/jpeg',
            })

            return {
                thumbnail_range: `${thumbnailStartPos}-${
                    thumbnailStartPos + thumbnail_size - 1
                }`,
                data,
            }
        } finally {
            this.#thumbnailModule._free(srcBuffer)
            this.#thumbnailModule._free(thumbnailBuffer)
            this.#thumbnailModule._free(dstBuffer)
            this.#thumbnailModule._free(dstSizeBuffer)
        }
    }

    #generateSnapshotBlobForEvent = async ({ video, isFree }) => {
        const smallSnapshotWidth = video.videoWidth
        const smallSnapshotHeight = video.videoHeight

        const snapshotCanvas = this.#generateScaledSnapshotCanvasFromVideo({
            video,
            width: smallSnapshotWidth,
            height: smallSnapshotHeight,
        })

        return new Promise((res, rej) => {
            snapshotCanvas.toBlob((blob) => {
                res(blob)
            }, 'image/jpeg')
        })
    }

    // for event snapshot
    #generateThumbnailBlobForEvent = async ({ video, isFree = true }) => {
        let smallSnapshotWidth = 128
        let smallSnapshotHeight = 96
        if (!isFree) {
            smallSnapshotWidth = 320
            smallSnapshotHeight = 240
        }

        const snapshotCanvas = this.#generateScaledSnapshotCanvasFromVideo({
            video,
            width: smallSnapshotWidth,
            height: smallSnapshotHeight,
        })

        return new Promise((res, rej) => {
            snapshotCanvas.toBlob(
                (blob) => {
                    res(blob)
                    // this.#downloadBlob({
                    // 	name: 'thumb.jpg',
                    // 	blob,
                    // });
                },
                'image/jpeg',
                '0.1'
            )
        })
    }

    #generateScaledSnapshotCanvasFromVideo = ({ video, width, height }) => {
        const {
            scaledWidth,
            scaledHeight,
            videoPosY,
            videoPosX,
        } = this.#scaleCanvasToVideoCenterAndKeepRatio({
            canvasHeight: height,
            canvasWidth: width,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
        })

        const snapshotCanvas = document.createElement('canvas')
        snapshotCanvas.width = width
        snapshotCanvas.height = height
        const snapshotContext = snapshotCanvas.getContext('2d')
        snapshotContext.drawImage(
            video,
            -1 * videoPosX,
            -1 * videoPosY,
            scaledWidth,
            scaledHeight
        )

        return snapshotCanvas
    }

    #scaleCanvasToVideoCenterAndKeepRatio = ({
        videoWidth,
        videoHeight,
        canvasWidth,
        canvasHeight,
    }) => {
        let scaledWidth = canvasWidth
        let scaledHeight = canvasHeight
        let videoPosY = 0
        let videoPosX = 0

        const videoRatio = videoWidth / videoHeight
        const canvasRatio = canvasWidth / canvasHeight

        if (videoRatio < canvasRatio) {
            scaledWidth = canvasWidth
            scaledHeight = videoHeight * (canvasWidth / videoWidth)
            videoPosX = (canvasWidth - scaledWidth) / 2
        } else {
            scaledHeight = canvasHeight
            scaledWidth = (videoWidth * canvasHeight) / videoHeight
            videoPosY = (canvasHeight - scaledHeight) / 2
        }

        return {
            scaledWidth,
            scaledHeight,
            videoPosY,
            videoPosX,
        }
    }

    // for test
    #downloadBlob = ({ blob, name }) => {
        const uri = URL.createObjectURL(blob)
        var link = document.createElement('a')
        link.download = name
        link.href = uri
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}
